import { Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import { formatToCNPJ } from 'brazilian-values';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import { AccountInfoLabel } from '@woovi/ui';
import { obscureCNPJ } from '@woovi/utils';

import type { PaymentLinkTransactionDetails_charge$key } from './__generated__/PaymentLinkTransactionDetails_charge.graphql.ts';
import type { PaymentLinkTransactionDetails_pixQrCode$key } from './__generated__/PaymentLinkTransactionDetails_pixQrCode.graphql.ts';

type PaymentLinkTransactionDetailsProps = {
  charge: PaymentLinkTransactionDetails_charge$key;
  pixQrCode: PaymentLinkTransactionDetails_pixQrCode$key;
};
const PaymentLinkTransactionDetails = (
  props: PaymentLinkTransactionDetailsProps,
): ReactNode => {
  const { t } = useTranslation();
  const charge = useFragment<PaymentLinkTransactionDetails_charge$key>(
    graphql`
      fragment PaymentLinkTransactionDetails_charge on Charge {
        identifier
        company {
          nameFriendly
          projectType
          taxID {
            taxID
          }
        }
        companyBankAccount {
          officialName
          taxID {
            taxID
          }
        }
      }
    `,
    props.charge,
  );

  const pixQrCode = useFragment<PaymentLinkTransactionDetails_pixQrCode$key>(
    graphql`
      fragment PaymentLinkTransactionDetails_pixQrCode on PixQrCode {
        identifier
        company {
          nameFriendly
          projectType
          taxID {
            taxID
          }
        }
      }
    `,
    props.pixQrCode,
  );

  const getCompany = () => {
    if (pixQrCode) {
      return pixQrCode?.company;
    }

    if (charge?.companyBankAccount) {
      return {
        nameFriendly: charge.companyBankAccount.officialName,
        taxID: charge.companyBankAccount.taxID,
      };
    }

    return charge.company;
  };

  const getText = () => {
    if (pixQrCode) {
      return pixQrCode?.identifier;
    }

    return charge?.identifier;
  };

  return (
    <>
      <Stack spacing={2} sx={{ marginBottom: '18px' }}>
        <Typography variant='h6' sx={{ fontWeight: 600, textAlign: 'center' }}>
          {t('Transaction details')}
        </Typography>
      </Stack>
      <AccountInfoLabel
        label={t('Addressee')}
        text={
          <>
            <Typography sx={{ fontSize: '16px', overflowWrap: 'anywhere' }}>
              {getCompany().nameFriendly}
            </Typography>
            <Typography sx={{ fontSize: '16px', overflowWrap: 'anywhere' }}>
              CNPJ:{' '}
              {obscureCNPJ(formatToCNPJ(getCompany()?.taxID?.taxID || ''))}
            </Typography>
          </>
        }
      />
      <AccountInfoLabel label={t('Identifier')} text={getText()} />
    </>
  );
};

export default PaymentLinkTransactionDetails;
